/**
 * Utility functions to convert HTML/CSS/JS code to React Live format
 */

/**
 * Convert HTML string to JSX format
 * @param {string} htmlCode - Raw HTML code
 * @returns {string} - JSX formatted code
 */
export const htmlToJSX = (htmlCode) => {
  if (!htmlCode) return '';
  
  return htmlCode
    // Convert class to className
    .replace(/class=/g, 'className=')
    // Convert for to htmlFor
    .replace(/for=/g, 'htmlFor=')
    // Convert style strings to objects
    .replace(/style="([^"]*)"/g, (match, styleString) => {
      const styleObj = styleString
        .split(';')
        .filter(Boolean)
        .reduce((acc, rule) => {
          const [property, value] = rule.split(':').map(s => s.trim());
          if (property && value) {
            // Convert kebab-case to camelCase
            const camelProperty = property.replace(/-([a-z])/g, (match, letter) => 
              letter.toUpperCase()
            );
            acc[camelProperty] = value;
          }
          return acc;
        }, {});
      
      return `style={${JSON.stringify(styleObj)}}`;
    })
    // Handle self-closing tags
    .replace(/<(img|br|hr|input|meta|link)([^>]*?)(?<!\/)\s*>/gi, '<$1$2 />')
    // Convert onclick, onchange, etc. to proper React event handlers
    .replace(/on(\w+)="([^"]*)"/g, (match, event, handler) => {
      const reactEvent = 'on' + event.charAt(0).toUpperCase() + event.slice(1);
      return `${reactEvent}={() => { ${handler} }}`;
    });
};

/**
 * Convert CSS to styled component or inline styles
 * @param {string} cssCode - Raw CSS code
 * @returns {string} - CSS formatted for React
 */
export const processCSS = (cssCode) => {
  if (!cssCode) return '';
  
  // Remove comments and clean up
  return cssCode
    .replace(/\/\*[\s\S]*?\*\//g, '') // Remove CSS comments
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Process JavaScript code for React Live environment
 * @param {string} jsCode - Raw JavaScript code
 * @returns {string} - Processed JavaScript code
 */
export const processJS = (jsCode) => {
  if (!jsCode) return '';
  
  return jsCode
    // Replace DOM queries with React refs if needed
    .replace(/document\.getElementById\(['"]([^'"]*)['"]\)/g, 'document.getElementById("$1")')
    // Wrap in try-catch for better error handling
    .replace(/^/, '// Component JavaScript\ntry {\n')
    .replace(/$/, '\n} catch (error) {\n  console.error("Component JS Error:", error);\n}');
};

/**
 * Generate React Live code from HTML, CSS, and JS
 * @param {string} htmlCode - Raw HTML code
 * @param {string} cssCode - Raw CSS code  
 * @param {string} jsCode - Raw JavaScript code
 * @returns {string} - Complete React component code
 */
export const generateReactLiveCode = (htmlCode, cssCode, jsCode) => {
  const jsxContent = htmlToJSX(htmlCode);
  const processedCSS = processCSS(cssCode);
  const processedJS = processJS(jsCode);

  return `function ComponentPreview() {
  // Component state and effects
  React.useEffect(() => {
    ${processedJS}
  }, []);

  return (
    <div className="component-preview-wrapper">
      <style jsx>{\`
        .component-preview-wrapper {
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 1rem;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
        }
        
        ${processedCSS}
      \`}</style>
      
      ${jsxContent || '<div>No content to preview</div>'}
    </div>
  );
}

render(<ComponentPreview />);`;
};

/**
 * Simple HTML to JSX converter for basic cases
 * @param {string} htmlCode - Raw HTML
 * @returns {string} - Basic JSX
 */
export const simpleHtmlToJSX = (htmlCode) => {
  if (!htmlCode) return '<div>No content</div>';
  
  // Convert HTML to JSX
  let jsxCode = htmlCode
    // Convert class to className
    .replace(/class=/g, 'className=')
    // Convert for to htmlFor
    .replace(/for=/g, 'htmlFor=')
    // Handle self-closing HTML and SVG tags
    .replace(/<(img|br|hr|input|meta|link|area|base|col|embed|source|track|wbr|path|circle|ellipse|line|rect|polygon|polyline|use|stop|animate|animateTransform|set)([^>]*?)(?<!\/)>/gi, '<$1$2 />')
    // Handle tags that might contain content but can also be self-closing in SVG
    .replace(/<(g|defs|clipPath|mask|pattern|marker|symbol|text|tspan|textPath|image|foreignObject)([^>]*?)><\/\1>/gi, '<$1$2 />')
    // Fix stroke-* attributes (convert kebab-case to camelCase for React)
    .replace(/stroke-([a-z]+[a-z-]*[a-z]+)=/g, (match, attr) => {
      const camelCase = attr.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      return `stroke${camelCase.charAt(0).toUpperCase() + camelCase.slice(1)}=`;
    })
    // Fix fill-rule to fillRule
    .replace(/fill-rule=/g, 'fillRule=')
    // Fix other common SVG attributes
    .replace(/clip-path=/g, 'clipPath=')
    .replace(/stroke-width=/g, 'strokeWidth=')
    .replace(/stroke-linecap=/g, 'strokeLinecap=')
    .replace(/stroke-linejoin=/g, 'strokeLinejoin=')
    .replace(/stroke-dasharray=/g, 'strokeDasharray=')
    .replace(/stroke-dashoffset=/g, 'strokeDashoffset=')
    .replace(/stroke-miterlimit=/g, 'strokeMiterlimit=')
    .replace(/font-family=/g, 'fontFamily=')
    .replace(/font-size=/g, 'fontSize=')
    .replace(/font-weight=/g, 'fontWeight=')
    .replace(/text-anchor=/g, 'textAnchor=')
    .replace(/mix-blend-mode=/g, 'mixBlendMode=');

  // Handle style attributes (convert to objects)
  jsxCode = jsxCode.replace(/style="([^"]*)"/g, (match, styleString) => {
    try {
      const styleObj = styleString
        .split(';')
        .filter(Boolean)
        .reduce((acc, rule) => {
          const [property, value] = rule.split(':').map(s => s.trim());
          if (property && value) {
            // Convert kebab-case to camelCase
            const camelProperty = property.replace(/-([a-z])/g, (match, letter) => 
              letter.toUpperCase()
            );
            acc[camelProperty] = value;
          }
          return acc;
        }, {});
      
      return `style={${JSON.stringify(styleObj)}}`;
    } catch (error) {
      // If conversion fails, return the original
      return match;
    }
  });

  // Handle event handlers
  jsxCode = jsxCode.replace(/on(\w+)="([^"]*)"/g, (match, event, handler) => {
    const reactEvent = 'on' + event.charAt(0).toUpperCase() + event.slice(1);
    return `${reactEvent}={() => { ${handler} }}`;
  });

  return jsxCode;
};
